/**
 * Mobile Responsiveness Utilities
 * Provides mobile-specific enhancements and touch optimizations
 */

export interface MobileDevice {
  isMobile: boolean
  isTablet: boolean
  isDesktop: boolean
  isTouch: boolean
  screenWidth: number
  screenHeight: number
  devicePixelRatio: number
  orientation: 'portrait' | 'landscape'
  connectionType: string
  isSlowConnection: boolean
  memoryInfo: {
    isLowMemory: boolean
    memoryLimit?: number
  }
}

export interface TouchGesture {
  type: 'tap' | 'double-tap' | 'long-press' | 'swipe' | 'pinch' | 'rotate'
  timestamp: number
  position: { x: number; y: number }
  velocity?: { x: number; y: number }
  distance?: number
  scale?: number
  rotation?: number
}

export class MobileEnhancer {
  private device: MobileDevice
  private lastTap: { x: number; y: number; time: number } | null = null
  private gestureCallbacks: Map<string, ((gesture: TouchGesture) => void)[]> = new Map()

  constructor() {
    this.device = this.detectDevice()
    this.setupEventListeners()
    this.enhanceForMobile()
  }

  private detectDevice(): MobileDevice {
    if (typeof window === 'undefined') {
      return this.getDefaultDevice()
    }

    const width = window.innerWidth
    const height = window.innerHeight
    const userAgent = navigator.userAgent.toLowerCase()

    // Device type detection
    const isMobile = width < 768 || /mobile|android|iphone|ipod|blackberry|iemobile|opera mini/i.test(userAgent)
    const isTablet = width >= 768 && width < 1024 || /ipad|tablet|kindle|silk/i.test(userAgent)
    const isDesktop = !isMobile && !isTablet
    const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0

    // Orientation detection
    const orientation = width > height ? 'landscape' : 'portrait'

    // Connection detection
    let connectionType = 'unknown'
    let isSlowConnection = false
    if ('connection' in navigator) {
      const connection = (navigator as any).connection
      connectionType = connection.effectiveType || 'unknown'
      isSlowConnection = connectionType === 'slow-2g' || connectionType === '2g'
    }

    // Memory detection
    let memoryInfo = { isLowMemory: false, memoryLimit: undefined as number | undefined }
    if ('deviceMemory' in navigator) {
      const deviceMemory = (navigator as any).deviceMemory
      memoryInfo = {
        isLowMemory: deviceMemory < 4,
        memoryLimit: deviceMemory
      }
    }

    return {
      isMobile,
      isTablet,
      isDesktop,
      isTouch,
      screenWidth: width,
      screenHeight: height,
      devicePixelRatio: window.devicePixelRatio || 1,
      orientation,
      connectionType,
      isSlowConnection,
      memoryInfo
    }
  }

  private getDefaultDevice(): MobileDevice {
    return {
      isMobile: false,
      isTablet: false,
      isDesktop: true,
      isTouch: false,
      screenWidth: 1920,
      screenHeight: 1080,
      devicePixelRatio: 1,
      orientation: 'landscape',
      connectionType: 'unknown',
      isSlowConnection: false,
      memoryInfo: { isLowMemory: false }
    }
  }

  private setupEventListeners() {
    if (typeof window === 'undefined') return

    // Handle orientation changes
    window.addEventListener('resize', () => {
      this.device = this.detectDevice()
    })

    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        this.device = this.detectDevice()
      }, 100)
    })
  }

  private enhanceForMobile() {
    if (!this.device.isMobile && !this.device.isTablet) return

    // Add mobile-specific CSS classes
    document.documentElement.classList.add('mobile-enhanced')

    if (this.device.isMobile) {
      document.documentElement.classList.add('is-mobile')
    } else if (this.device.isTablet) {
      document.documentElement.classList.add('is-tablet')
    }

    // Add touch class
    if (this.device.isTouch) {
      document.documentElement.classList.add('touch-device')
    }

    // Optimize for slow connections
    if (this.device.isSlowConnection) {
      document.documentElement.classList.add('slow-connection')
    }

    // Add orientation class
    document.documentElement.classList.add(`orientation-${this.device.orientation}`)
  }

  public getDevice(): MobileDevice {
    return { ...this.device }
  }

  public addGestureListener(type: string, callback: (gesture: TouchGesture) => void) {
    if (!this.gestureCallbacks.has(type)) {
      this.gestureCallbacks.set(type, [])
    }
    this.gestureCallbacks.get(type)!.push(callback)
  }

  public removeGestureListener(type: string, callback: (gesture: TouchGesture) => void) {
    const callbacks = this.gestureCallbacks.get(type)
    if (callbacks) {
      const index = callbacks.indexOf(callback)
      if (index > -1) {
        callbacks.splice(index, 1)
      }
    }
  }

  private fireGesture(type: TouchGesture['type'], gesture: Omit<TouchGesture, 'type' | 'timestamp'>) {
    const fullGesture: TouchGesture = {
      type,
      timestamp: Date.now(),
      ...gesture
    }

    const callbacks = this.gestureCallbacks.get(type)
    if (callbacks) {
      callbacks.forEach(callback => callback(fullGesture))
    }
  }

  // Setup touch event handlers for elements
  public setupTouchHandlers(element: HTMLElement) {
    if (!this.device.isTouch) return

    let startTime = 0
    let startX = 0
    let startY = 0

    element.addEventListener('touchstart', (e) => {
      startTime = Date.now()
      const touch = e.touches[0]
      startX = touch.clientX
      startY = touch.clientY

      // Store touch start position for gesture detection
      // this.touchStart was removed, using local variables instead
    }, { passive: true })

    element.addEventListener('touchend', (e) => {
      const endTime = Date.now()
      const touch = e.changedTouches[0]
      const endX = touch.clientX
      const endY = touch.clientY

      const duration = endTime - startTime
      const deltaX = endX - startX
      const deltaY = endY - startY
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY)

      // Detect tap
      if (duration < 200 && distance < 10) {
        // Check for double tap
        if (this.lastTap && endTime - this.lastTap.time < 300) {
          const tapDistance = Math.sqrt(
            Math.pow(endX - this.lastTap.x, 2) + Math.pow(endY - this.lastTap.y, 2)
          )
          if (tapDistance < 30) {
            this.fireGesture('double-tap', { position: { x: endX, y: endY } })
            this.lastTap = null
            return
          }
        }

        this.lastTap = { x: endX, y: endY, time: endTime }
        this.fireGesture('tap', { position: { x: endX, y: endY } })
      }
      // Detect long press
      else if (duration >= 500 && distance < 10) {
        this.fireGesture('long-press', { position: { x: startX, y: startY } })
      }
      // Detect swipe
      else if (duration < 1000 && distance > 50) {
        const velocity = {
          x: deltaX / duration,
          y: deltaY / duration
        }
        this.fireGesture('swipe', {
          position: { x: endX, y: endY },
          velocity,
          distance
        })
      }
    }, { passive: true })
  }

  public optimizeImagesForMobile() {
    if (!this.device.isMobile) return

    // Lazy load images for mobile
    const images = document.querySelectorAll('img[data-src]')
    images.forEach(img => {
      const imgElement = img as HTMLImageElement
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver((entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              imgElement.src = imgElement.getAttribute('data-src') || ''
              imgElement.removeAttribute('data-src')
              observer.unobserve(imgElement)
            }
          })
        })
        observer.observe(imgElement)
      }
    })
  }

  public getOptimalVideoConstraints(): MediaStreamConstraints {
    if (!this.device.isMobile) {
      return {
        video: {
          width: { ideal: 1920 },
          height: { ideal: 1080 },
          facingMode: 'user'
        },
        audio: true
      }
    }

    // Mobile constraints
    const constraints: MediaStreamConstraints = {
      video: {
        width: { ideal: 1280, max: 1920 },
        height: { ideal: 720, max: 1080 },
        facingMode: 'user',
        frameRate: { ideal: 30, max: 60 }
      },
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    }

    // Adjust for slow connections
    if (this.device.isSlowConnection) {
      constraints.video = {
        width: { ideal: 640, max: 1280 },
        height: { ideal: 480, max: 720 },
        facingMode: 'user',
        frameRate: { ideal: 15, max: 30 }
      }
    }

    // Adjust for low memory devices
    if (this.device.memoryInfo.isLowMemory) {
      constraints.video = {
        width: { ideal: 480, max: 720 },
        height: { ideal: 360, max: 480 },
        facingMode: 'user',
        frameRate: { ideal: 15, max: 30 }
      }
    }

    return constraints
  }
}

// Singleton instance
export const mobileEnhancer = new MobileEnhancer()

// Export convenience functions
export const getDeviceInfo = () => mobileEnhancer.getDevice()
export const setupMobileHandlers = (element: HTMLElement) => mobileEnhancer.setupTouchHandlers(element)
export const getMobileVideoConstraints = () => mobileEnhancer.getOptimalVideoConstraints()